import '@mdxeditor/editor/style.css'
import { MDXEditor, MDXEditorMethods } from '@mdxeditor/editor'
import { Box, Button, FormControl, InputLabel, Link, MenuItem, Select, TextField } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react';
import { Client } from '../data/Client';
import { useRootStore } from '../hooks/useRootStore';
import { Project } from '../data/Project';
import type { Issue } from '../data/Issue.ts';
import { IssueState } from '../data/IssueState';
import moment from 'moment';
import { getIssuePriorityNameRu } from '../data/IssuePriority.ts';
import { MdxPlugins } from './MarkdownEditor/MarkdownEditorPlugins';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Skill } from '../data/Skill.ts';
import { Person } from '../data/Person.ts';
import { IssueListFilterModel, IssueListItem } from './IssueList.tsx';

const IssueEdit = ({ issue, onSave, onCancel }: { issue: Issue, onSave: (changed: Issue) => void, onCancel: () => void }) => {
    const [changed, setChanged] = useState(issue);

    const mobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const [clientId, setClientId] = useState(issue.Project?.ClientID);
    const [clients, setClients] = useState<Client[]>([] as Client[]);
    const [projects, setProjects] = useState<Project[]>([] as Project[]);

    const [skills, setSkills] = useState<Skill[]>([]);

    const { api, userProfile } = useRootStore();

    // не-нативный MUI select игнорит required, поэтому проверка "вручную"
    const [clientNotSpecified, setClientNotSpecified] = useState(false);
    const [projectNotSpecified, setProjectNotSpecified] = useState(false);
    const [subjectNotSpecified, setSubjectNotSpecified] = useState(false);
    const [lastCreatedIssue, setLastCreatedIssue] = useState<IssueListItem | null>(null);

    /* useEffect(() => {
        document.onpaste = function (event) {
            alert('111');
            // use event.originalEvent.clipboard for newer chrome versions
            const items = (event.clipboardData || event.originalEvent.clipboardData)?.items;
            console.log(JSON.stringify(items)); // will give you the mime types
            // find pasted image among pasted items            
            if (items == null) {
                alert('null');
                return;
            }
                
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf("image") === 0) {
                    alert('123');
                    const blob = items[i].getAsFile();
                    if (blob !== null) {
                        const reader = new FileReader();
                        reader.onload = function (event) {
                            console.log(event.target.result); // data url!
                        };
                        reader.readAsDataURL(blob);
                    }
                }
            }   
        }
    }, []);*/

    useEffect(() => {
        if (changed.StateID === IssueState.stub) {

            api.getSkills().then(skills => {
                setSkills(skills);
            });

            api.getClients().then(clients => {
                setClients(clients.sort((a, b) => a.Name.localeCompare(b.Name)));
            });
        }
    }, [api, changed.StateID]);


    useEffect(() => {
        if (changed.StateID === IssueState.stub && userProfile) {
            api.getIssues({
                authors: [{ ID: userProfile.ID } as Person],
                datesCreate: { from: new Date(new Date().setMonth(new Date().getMonth() - 3)), to: null },
                orderBy: 'CreationDate desc',
                states: (Object.values(IssueState) as IssueState[]).filter(x => x !== IssueState.stub && !isNaN(Number(x))),
            } as IssueListFilterModel, 1)
                .then(issues => setLastCreatedIssue(issues.Records[0]));
        }
    }, [api, changed.StateID, userProfile]);


    useEffect(() => {
        if (changed.StateID === IssueState.stub) {
            if (!clientId) {
                setProjects([] as Project[]);
                if (clients.length == 1) {
                    setClientId(clients[0].ID);
                }
                else if (lastCreatedIssue && clients.filter(x => x.ID === lastCreatedIssue.ClientID).length) {
                    setClientId(lastCreatedIssue.ClientID);
                }
            } else {
                const client = clients.filter(x => x.ID === clientId)[0] ?? null;
                if (client) {
                    const clientProjects = client.Projects;
                    setProjects(client.Projects);
                    setChanged({
                        ...changed,
                        Project: clientProjects.length == 1 ?
                            { ClientID: client.ID, ClientName: client.Name, ID: clientProjects[0].ID, Name: clientProjects[0].Name } as Project
                            : lastCreatedIssue == null || clientProjects.filter(x => x.ID == lastCreatedIssue.ProjectID).length == 0
                                ? null
                                : {
                                    ClientID: client.ID,
                                    ClientName: client.Name,
                                    ID: lastCreatedIssue.ProjectID,
                                    Name: lastCreatedIssue.ProjectName,
                                } as Project
                    });
                }
                else {
                    setProjects([] as Project[]);
                };
            }
        }
    }, [clients, clientId, changed.StateID, lastCreatedIssue]);

    const handleSkillIDChange = useCallback((newID: number) => {
        const _skill = skills.filter(x => x.ID == newID);
        if (_skill.length > 0) {
            setChanged({ ...changed, RequiredSkillID: newID, RequiredSkillName: _skill[0].Name });
        };
    }, [skills, changed]);

    useEffect(() => {
        if (changed.StateID === IssueState.stub
            && !changed.RequiredSkillID
            && lastCreatedIssue?.SkillID
            && skills.length) {
            handleSkillIDChange(lastCreatedIssue.SkillID);
        }
    }, [skills.length, lastCreatedIssue, changed.StateID, changed.RequiredSkillID, handleSkillIDChange]);

    const mdxEditorRef = useRef<MDXEditorMethods>(null);

    const checkIssue = (): boolean => {
        let result = true;

        const issueText = mdxEditorRef.current?.getMarkdown() ?? '';
        setChanged({ ...changed, IssueText: issueText });

        if (!clientId) {
            setClientNotSpecified(true);
            result = false;
        }
        if (!changed.Project) {
            setProjectNotSpecified(true);
            result = false;
        }
        if (changed.Subject.trim().length == 0) {
            setSubjectNotSpecified(true);
            result = false;
        }

        return result;
    }

    const submitIssue = () => {
        if (checkIssue()) {
            const issueText = mdxEditorRef.current?.getMarkdown() ?? '';
            const ch = { ...changed, IssueText: issueText, StateID: IssueState.backlog };

            api.submitIssue(ch).then(() => onSave(ch));
        }
    }

    const updateIssueText = () => {
        if (checkIssue()) {
            const issueText = mdxEditorRef.current?.getMarkdown() ?? '';
            const ch = { ...changed, IssueText: issueText };
            api.updateIssueText(issue.ID, changed.Subject, issueText).then(() => onSave(ch));
        }
    }

    const handleClientIDChange = (newID: number) => {
        setClientNotSpecified(false);
        const clientProjects = (clients.filter(x => x.ID == newID)[0]).Projects;
        setProjects(clientProjects);
        setClientId(newID);
        if (clientProjects.length == 1) {
            setProjectNotSpecified(false);
            setChanged({ ...changed, Project: { ClientID: clientProjects[0].ClientID, ClientName: clientProjects[0].ClientName, ID: clientProjects[0].ID, Name: clientProjects[0].Name } as Project });
        }
        else {
            setChanged({ ...changed, Project: null });
        }
    }

    const handleProjectIDChange = (newID: number) => {
        setProjectNotSpecified(false);
        clients.forEach((client: Client) => {
            client.Projects.forEach((project: Project) => {
                if (project.ID === newID) {
                    setChanged({ ...changed, Project: project });
                }
            });
        });
    }

    return (
        <Box>
            <TextField
                id="issue-title"
                label="Заголовок"
                variant="outlined"
                required
                error={subjectNotSpecified}
                value={changed.Subject ?? ''}
                sx={{ width: '100%', mb: 2, mt: 2 }}
                onChange={(e) => {
                    setChanged({
                        ...changed, Subject: e.target.value
                    })
                    setSubjectNotSpecified(e.target.value.trim().length === 0);
                }}
            />
            {changed.StateID == IssueState.stub &&
                <>
                    <Box sx={{ mb: 2 }} >Задача: <b>{changed.ID}</b>. Создана: <b>{moment(changed.CreationDate).format('DD.MM.YYYY')}</b>. Заявитель: <Link href={`./employees/${changed.AuthorID}`} title="Открыть карточку сотрудника">{changed.AuthorName}</Link> Приоритет: <b>{getIssuePriorityNameRu(changed.PriorityID)}</b>. Ошибка: <b>{changed.IsError ? 'Да' : 'Нет'}</b></Box>
                    <Box sx={{ display: 'flex', mb: 3 }}>
                        <FormControl
                            sx={{ mt: 1, mr: 1, width: "100%", minWidth: 120 }}
                            error={clientNotSpecified}
                        >
                            <InputLabel>Клиент</InputLabel>
                            <Select
                                label='Клиент'
                                id="client-disabled"
                                value={clientId ?? ''}
                                disabled={clients.length < 2}
                                onChange={(e) => handleClientIDChange(e.target.value as number)}
                            >
                                {clients.map((c) => <MenuItem key={c.ID} value={c.ID}>{c.Name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl
                            sx={{ mt: 1, mr: 1, width: "100%", minWidth: 120 }}
                            error={projectNotSpecified}
                        >
                            <InputLabel>Проект</InputLabel>
                            <Select
                                id="project-select"
                                defaultValue={0}
                                value={changed.Project?.ID ?? ''}
                                label='Проект'
                                required
                                onChange={(e) => handleProjectIDChange(e.target.value as number)}
                                disabled={(projects?.length ?? 0) < 2}
                            >
                                {projects.map((p) => <MenuItem key={p.ID} value={p.ID}>{p.Name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ mt: 1, width: "100%", minWidth: 120 }}>
                            <InputLabel>Направление</InputLabel>
                            <Select
                                label='Направление'
                                id="skill-select"
                                value={changed.RequiredSkillID ?? ''}
                                disabled={skills.length < 2}
                                onChange={(e) => handleSkillIDChange(e.target.value as number)}
                            >
                                {skills.map((s) => <MenuItem key={s.ID} value={s.ID}>{s.Name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                    <TextField label="Клиентский трекер (ссылка)" sx={{ mb: 2, width: '100%' }} onChange={(e) => setChanged({ ...changed, ExternalTrackerLink: e.target.value })} />
                </>
            }
            <Box sx={{ textAlign: 'left', minHeight: '300px', }} onClick={() => mdxEditorRef.current?.focus()}>
                <MDXEditor
                    ref={mdxEditorRef}
                    markdown={changed.IssueText ?? ''}
                    plugins={MdxPlugins.standardPlugins(api, issue.ID, issue.IssueText, mobile)}
                />
            </Box>
            <Box sx={{ display: 'flex' }} >
                <Button onClick={() => { if (changed.StateID == IssueState.stub) submitIssue(); else updateIssueText(); }}>Сохранить</Button>
                <Button onClick={() => onCancel()}>Отмена</Button>
            </Box>
        </Box>
    )
}

export default IssueEdit;